import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalFooterActionsComponent } from '../modal-footer-actions/modal-footer-actions.component';

/**
 * Modal dialog component used to display alerts, confirmations, or warnings with configurable title, descriptions, and primary/secondary actions.
 * @component
 * @selector app-alert-modal
 * @example
 *   <app-alert-modal
 *     [isOpen]="isModalOpen"
 *     [title]="'Confirm deletion'"
 *     [description]="'This action cannot be undone.'"
 *     (close)="onClose()"
 *     (primaryAction)="onConfirm()"
 *     (secondaryAction)="onCancel()">
 *   </app-alert-modal>
 * @input boolean isOpen - Controls whether the modal is visible (true = open).
 * @input string title - Main title displayed at the top of the modal.
 * @input string description - Primary descriptive text shown in the modal body.
 * @input string additionalDescription - Optional secondary descriptive text or details.
 * @input string primaryButtonText - Label for the primary action button (default: 'Confirm').
 * @input string secondaryButtonText - Label for the secondary action button (default: 'Cancel').
 * @input string primaryButtonClass - CSS class(es) applied to the primary action button (default: 'btn-primary').
 * @input string secondaryButtonClass - CSS class(es) applied to the secondary action button (default: 'btn-secondary').
 * @input boolean showCloseIcon - Whether to display a close (X) icon in the modal header.
 * @input boolean showWarning - Whether to display a warning state or icon alongside the message.
 * @input string warningMessage - Text for an optional warning message displayed when showWarning is true.
 * @output EventEmitter<void> close - Emitted when the modal is requested to close (overlay click, close icon, or programmatic close).
 * @output EventEmitter<void> primaryAction - Emitted when the primary action button is clicked.
 * @output EventEmitter<void> secondaryAction - Emitted when the secondary action button is clicked.
 * Key methods:
 *   - onOverlayClick(event: Event): Closes the modal only when the overlay itself is clicked (prevents closing when clicking modal content).
 *   - onClose(): Emits the close event to notify parent components to hide the modal.
 *   - onPrimaryAction(): Emits the primaryAction event when the primary button is activated.
 *   - onSecondaryAction(): Emits the secondaryAction event when the secondary button is activated.
 * Lifecycle:
 *   - No internal Angular lifecycle hooks are implemented; visibility and content are driven via @Input properties and parent component state.
 */
@Component({
  selector: 'app-alert-modal',
  standalone: true,
  imports: [CommonModule, ModalFooterActionsComponent],
  templateUrl: './alert-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./alert-modal.component.scss'],
})
export class AlertModalComponent {
  @Input() isOpen = false;
  @Input() title = '';
  @Input() description = '';
  @Input() additionalDescription = '';
  @Input() primaryButtonText = 'Confirm';
  @Input() secondaryButtonText = 'Cancel';
  @Input() primaryButtonClass = 'btn-primary';
  @Input() secondaryButtonClass = 'btn-secondary';
  @Input() showCloseIcon = true;
  @Input() showWarning = false;
  @Input() warningMessage = '';

  @Output() close = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  onOverlayClick(event: Event): void {
    // Only close if clicking directly on the overlay, not on the modal content
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onPrimaryAction(): void {
    this.primaryAction.emit();
  }

  onSecondaryAction(): void {
    this.secondaryAction.emit();
  }
}
